import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Color scheme matching Bill PDF (dark brown, gold theme)
const DARK_BROWN = [59, 36, 16];
const GOLD       = [201, 168, 76];
const LIGHT_GOLD = [255, 255, 255]; // white
const BLACK      = [0, 0, 0];
const GREY_LIGHT = [245, 245, 245];
const GREY_MID   = [200, 200, 200];
const BLUE_LINK  = [0, 102, 204];
const CREAM_BG   = [245, 240, 230];

function buildDoc(paymentData, companyData = {}, masterData = {}, bankName = '', paymentMethodName = 'Cash', tableData = [], companyId = null) {
  const doc = new jsPDF('p', 'mm', 'a4'); // Portrait mode
  const pw  = doc.internal.pageSize.getWidth();
  const ph  = doc.internal.pageSize.getHeight();
  const lm  = 10; // left margin
  const rm  = pw - lm; // right margin

  // CompanyId=2 hides company branding
  const hideCompanyDetails = Number(companyId) === 2;

  const companyName    = companyData.Name           || 'COCHIN FRAMES';
  const companyPhone1  = companyData.ContactNumber1 || '0484-4864027';
  const companyPhone2  = companyData.ContactNumber2 || '7356864227';
  const companyPhone3  = companyData.ContactNumber3 || '9037559716';
  const companyEmail   = companyData.Email          || 'cochinframes07@gmail.com';
  const companyWeb     = companyData.Website        || 'www.cochinframes.com';
  const companyAddr1   = companyData.AddressLine1   || 'Edappally -';
  const companyAddr2   = companyData.AddressLine2   || 'Tripunithura Road,';
  const companyCity    = companyData.Location       || 'Padivattom, Kochi-24.';
  const companyGST     = companyData.GSTNumber      || '';

  const isPayment = !paymentData.IsType; // IsType false = Payment, true = Receipt
  const documentType = paymentData.documentType || (isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER');
  const partyLabel = isPayment ? 'Paid To' : 'Received From';

  const masterName = paymentData.MasterName || masterData.Name || '-';
  const masterAddress = masterData.Address || '';
  const masterGST = masterData.GSTNumber || '';
  const masterContact = masterData.ContactNumber || masterData.Mobile || '';

  // Top bar (slim, matching Bill PDF)
  doc.setFillColor(...CREAM_BG);
  doc.rect(0, 0, pw, 4, 'F');

  if (!hideCompanyDetails) {
    // Company name
    doc.setFontSize(23);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_BROWN);
    doc.text(companyName.toUpperCase(), pw / 2, 14, { align: 'center' });

    const nameWidth = doc.getTextWidth(companyName.toUpperCase());
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.6);
    doc.line(pw / 2 - nameWidth / 2 - 2, 16, pw / 2 + nameWidth / 2 + 2, 16);

    // Company tagline
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 70, 30);
    doc.text(
      'Photo Framing  |  Certificate Framing  |  Artwork Framing  |  Photo Restoration  |  Collage Framing',
      pw / 2, 21, { align: 'center' }
    );

    // Company contact info (centered, smaller)
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const contactLine1 = `${companyAddr1} ${companyAddr2} ${companyCity}`;
    const contactLine2 = `Tel: ${companyPhone1} | Mob: ${companyPhone2}, ${companyPhone3}`;
    const contactLine3 = `Email: ${companyEmail} | Web: ${companyWeb}`;
    doc.text(contactLine1, pw / 2, 25, { align: 'center' });
    doc.text(contactLine2, pw / 2, 28.5, { align: 'center' });
    doc.text(contactLine3, pw / 2, 32, { align: 'center' });
    if (companyGST) {
      doc.text(`GST: ${companyGST}`, pw / 2, 35.5, { align: 'center' });
    }
  }

  // Document type banner (matching Bill PDF style)
  const bannerY = hideCompanyDetails ? 8 : (companyGST ? 39 : 35.5);
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(lm, bannerY, pw - lm * 2, 9, 2, 2, 'S');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text(documentType, pw / 2, bannerY + 6.5, { align: 'center' });

  // Party & Voucher info box (matching Bill PDF structure)
  const boxY = bannerY + 12;
  const boxH = 32;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm, boxY, pw - lm * 2, boxH, 2, 2, 'S');

  // Vertical divider (60/40 split for party info / voucher details)
  const dividerX = lm + (pw - lm * 2) * 0.6;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.2);
  doc.line(dividerX, boxY + 1, dividerX, boxY + boxH - 1);

  // Left section: Party information
  const infoY = boxY + 6;
  const gap   = 5;
  const lc = lm + 3;
  // Use dynamic value-X based on actual label width instead of a fixed offset
  const getValueX = (doc, label, fontSize = 9.5) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');
    return lc + doc.getTextWidth(label) + 3;
  };

  doc.setFontSize(9.5);
  infoRow(doc, lc, getValueX(doc, partyLabel), infoY, partyLabel, masterName);
  if (masterAddress) {
    const addrLabelW = getValueX(doc, partyLabel);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...BLACK);
    const addrLines = doc.splitTextToSize(masterAddress, dividerX - addrLabelW - 5);
    doc.text(addrLines, addrLabelW, infoY + gap);
  }
  const nextY = masterAddress ? infoY + gap + 8 : infoY + gap;
  if (masterGST) {
    infoRow(doc, lc, getValueX(doc, 'GST No'), nextY, 'GST No', masterGST);
  }
  if (masterContact) {
    infoRow(doc, lc, getValueX(doc, 'Contact'), nextY + (masterGST ? gap : 0), 'Contact', masterContact);
  }

  // Right section: Voucher details
  const rc = dividerX + 3;
  const getRValueX = (doc, label, fontSize = 9.5) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');
    return rc + doc.getTextWidth(label) + 3;
  };

  doc.setFontSize(9.5);
  infoRow(doc, rc, getRValueX(doc, 'Voucher No'), infoY, 'Voucher No', paymentData.BillNumber || '-');
  infoRow(doc, rc, getRValueX(doc, 'Date'), infoY + gap, 'Date', fmtDate(paymentData.BillDate));
  infoRow(doc, rc, getRValueX(doc, 'Fin Year'), infoY + gap * 2, 'Fin Year', paymentData.FinYear || '-');
  infoRow(doc, rc, getRValueX(doc, 'Mode'), infoY + gap * 3, 'Mode', paymentMethodName);

  let detailY = infoY + gap * 4;
  if (paymentData.ChequeNumber) {
    infoRow(doc, rc, getRValueX(doc, 'Ref No'), detailY, 'Ref No', paymentData.ChequeNumber);
    detailY += gap;
  }
  if (bankName) {
    infoRow(doc, rc, getRValueX(doc, 'Bank'), detailY, 'Bank', bankName);
  }

  // Transaction details table
  const tableStartY = boxY + boxH + 4;

  autoTable(doc, {
    startY: tableStartY,
    head: [['Sr.No', 'Particulars', 'Description', 'Amount']],
    body: tableData,
    theme: 'grid',
    tableWidth: pw - lm * 2,
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
      lineColor: GOLD,
      lineWidth: 0.15,
      textColor: [30, 30, 30],
      overflow: 'linebreak',
      font: 'helvetica',
    },
    headStyles: {
      fillColor: false,
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 10,
      lineWidth: 0.2,
      lineColor: GOLD,
      minCellHeight: 7,
    },
    alternateRowStyles: { fillColor: GREY_LIGHT },
    columnStyles: {
      0: { halign: 'center', cellWidth: 20 },  // Sr.No
      1: { halign: 'left',   cellWidth: 50 },  // Particulars
      2: { halign: 'left',   cellWidth: 70 },  // Description
      3: { halign: 'right',  cellWidth: 50 },  // Amount
    },
    margin: { left: lm, right: lm },
  });

  // Amount summary section
  const afterTable = (doc.lastAutoTable?.finalY ?? tableStartY + 40) + 4;
  
  // Amount in words box (full width)
  const wordsBoxH = 18;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm, afterTable, pw - lm * 2, wordsBoxH, 2, 2, 'S');

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.text('Amount in Words:', lm + 3, afterTable + 5);
  
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  const amountInWords = `Rupees ${numberToWords(paymentData.TotalAmount || paymentData.Amount || 0)} Only`;
  const wordsLines = doc.splitTextToSize(amountInWords, pw - lm * 2 - 8);
  doc.text(wordsLines, lm + 3, afterTable + 10);

  // Remarks section (if present)
  let remarksY = afterTable + wordsBoxH + 3;
  if (paymentData.Remarks) {
    const remarksBoxH = 14;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.roundedRect(lm, remarksY, pw - lm * 2, remarksBoxH, 2, 2, 'S');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_BROWN);
    doc.text('Remarks:', lm + 3, remarksY + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BLACK);
    const remarksLines = doc.splitTextToSize(paymentData.Remarks, pw - lm * 2 - 8);
    doc.text(remarksLines, lm + 3, remarksY + 10);
    remarksY += remarksBoxH + 3;
  }

  // Amount breakdown box
  const breakdownY = remarksY;
  const breakdownH = 35;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm, breakdownY, pw - lm * 2, breakdownH, 2, 2, 'S');

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.text('Amount Breakdown', lm + 3, breakdownY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);

  let amountY = breakdownY + 10;
  const amountData = [
    ['Basic Amount', fmt(paymentData.Amount || 0)],
  ];

  if (paymentData.CGSTAmount > 0) {
    amountData.push(['CGST', fmt(paymentData.CGSTAmount)]);
  }
  if (paymentData.SGSTAmount > 0) {
    amountData.push(['SGST', fmt(paymentData.SGSTAmount)]);
  }
  if (paymentData.IGSTAmount > 0) {
    amountData.push(['IGST', fmt(paymentData.IGSTAmount)]);
  }
  if (paymentData.RoundOff && paymentData.RoundOff !== 0) {
    amountData.push(['Round Off', fmt(paymentData.RoundOff)]);
  }

  amountData.forEach(([label, value]) => {
    doc.text(label, lm + 5, amountY);
    doc.text(value, rm - 5, amountY, { align: 'right' });
    amountY += 4;
  });

  // Total amount with gold background
  const totalY = breakdownY + breakdownH - 8;
  doc.setDrawColor(...GOLD);
  doc.setFillColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm + 2, totalY - 2, pw - lm * 2 - 4, 6, 1, 1, 'FD');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...LIGHT_GOLD);
  doc.text('Total Amount', lm + 5, totalY + 2);
  doc.text(fmt(paymentData.TotalAmount || paymentData.Amount || 0), rm - 5, totalY + 2, { align: 'right' });

  // Signature section
  const sigY = breakdownY + breakdownH + 6;
  const sigH = 20;
  const sigW = (pw - lm * 2) / 3;

  ['PREPARED BY', 'CHECKED BY', 'RECEIVED BY'].forEach((title, idx) => {
    const x = lm + (idx * sigW);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, sigY, sigW, sigH, 2, 2, 'S');

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLACK);
    doc.text(title, x + sigW / 2, sigY + sigH - 3, { align: 'center' });
  });

  // For Company + Authorized Signatory text (below signature boxes)
  if (!hideCompanyDetails) {
    const authY = sigY + sigH + 10;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_BROWN);
    doc.setFontSize(10);
    doc.text(`For ${companyName}`, rm, authY, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLACK);
    doc.text('Authorised Signatory', rm, authY + 15, { align: 'right' });
  }

  // Footer section
  const fy = ph - 12;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(lm, fy - 3, rm, fy - 3);

  // Bottom bar (cream background)
  doc.setFillColor(...CREAM_BG);
  doc.rect(0, ph - 8, pw, 8, 'F');

  if (!hideCompanyDetails) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 40, 10);
    doc.text(`© ${companyName} — Quality Framing Since 2000`, pw / 2, ph - 3.5, { align: 'center' });
  }

  return doc;
}

// Main export function for printing
export const printPaymentReceiptPDF = (paymentData, companyData = {}, masterData = {}, bankName = '', paymentMethodName = 'Cash', tableData = [], companyId = null) => {
  const doc = buildDoc(paymentData, companyData, masterData, bankName, paymentMethodName, tableData, companyId);
  
  // Auto print
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  const win = window.open(blobUrl, '_blank');
  if (win) {
    win.addEventListener('load', () => {
      win.focus();
      win.print();
    });
  } else {
    // Fallback download if popup blocked
    const filename = `${paymentData.IsType ? 'Payment' : 'Receipt'}_${paymentData.BillNumber || 'Voucher'}.pdf`;
    doc.save(filename);
  }
};

// Export function for downloading
export const downloadPaymentReceiptPDF = (paymentData, companyData = {}, masterData = {}, bankName = '', paymentMethodName = 'Cash', tableData = [], companyId = null) => {
  const doc = buildDoc(paymentData, companyData, masterData, bankName, paymentMethodName, tableData, companyId);
  const filename = `${paymentData.IsType ? 'Payment' : 'Receipt'}_${paymentData.BillNumber || 'Voucher'}.pdf`;
  doc.save(filename);
};

// ─── Helper Functions ──────────────────────────────────────────────────────────

function infoRow(doc, labelX, valueX, y, label, value) {
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(label + ' :', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(String(value), valueX, y);
}

function fmt(val) {
  return parseFloat(val || 0).toFixed(2);
}

function fmtDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${d.toLocaleString('en-GB', { month: 'short' })}/${d.getFullYear()}`;
}

function numberToWords(num) {
  if (num === 0 || num === '0') return 'Zero';
  
  const amount = parseFloat(num);
  if (isNaN(amount)) return 'Zero';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

  function convertLessThanThousand(n) {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
  }

  function convertIndianNumbering(n) {
    if (n === 0) return 'Zero';
    
    let crore = Math.floor(n / 10000000);
    n %= 10000000;
    let lakh = Math.floor(n / 100000);
    n %= 100000;
    let thousand = Math.floor(n / 1000);
    n %= 1000;
    let remainder = n;

    let result = '';
    if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
    if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
    if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
    if (remainder > 0) result += convertLessThanThousand(remainder);

    return result.trim();
  }

  const integerPart = Math.floor(amount);
  const decimalPart = Math.round((amount - integerPart) * 100);

  let words = convertIndianNumbering(integerPart);

  if (decimalPart > 0) {
    words += ' and ' + convertLessThanThousand(decimalPart) + ' Paise';
  }

  return words;
}
